// CELL-BIO9 — AI client. The ONLY file that calls /api/narrate.
// Streams SSE from the proxy, feeds text chunks directly into the typewriter
// queue, and applies bounded effects on the final "done" event.
// If the request fails or AI is disabled, the static fallback is used instead.

import type { Effects } from "./types";
import type { Terminal } from "./terminal";
import { gameState } from "./gameState";

/** Shape of the final SSE event from the proxy. */
interface AIDoneEvent {
  effects?: Effects;
  flags_add?: string[];
  done: true;
}

/** Per-scene static fallback text (displayed when AI is unavailable). */
const FALLBACKS: Record<string, string> = {
  ghost_freeform:
    "Ghost listens. His expression doesn't change. \"Interesting,\" he says, finally. \"Come find me when you know what you actually want.\"",
  tank_yard_talk:
    "Tank nods once. \"Alright.\" He goes back to watching the yard. That's all you're getting today.",
  riley_favor:
    "Riley glances over his shoulder. \"Look, I didn't say I could do anything. I'm just saying — maybe. Check back tomorrow.\"",
  rowan_fence:
    "Rowan smiles like he's selling something. \"We'll talk. When the time's right. Everything has a price, friend — including patience.\"",
  vera_call:
    "Vera's voice is clipped, precise. \"I hear you. I need more time. Don't call this number again until I reach out first.\"",
  brick_confrontation:
    "Brick won't meet your eyes. His jaw works. He says nothing. The silence is its own kind of answer.",
  crane_meeting:
    "Crane straightens her jacket. \"This conversation isn't happening.\" She turns and walks. Her heels are very loud on the marble.",
  okafor_office:
    "Warden Okafor sets down his pen. \"I'll note that in your file, Mr. Vale.\" His expression gives nothing away.",
  voss_endgame:
    "The line is quiet for a long moment. Then: \"Think about it. You know where to find me.\"",
  cell_ambient:
    "The cell is four paces long. The walls remember everyone who counted them before you.",
};

/** Maximum stat delta the client will accept from AI (server enforces same). */
const MAX_STAT_DELTA = 2;

function clampEffects(effects: Effects | undefined): Effects {
  if (!effects) return {};
  const out: Effects = {};
  for (const key of ["rep", "intel", "muscle", "heat"] as const) {
    const v = effects[key];
    if (typeof v === "number") {
      out[key] = Math.max(-MAX_STAT_DELTA, Math.min(MAX_STAT_DELTA, Math.round(v)));
    }
  }
  return out;
}

export interface AISceneRequest {
  scene: string;
  playerInput: string;
}

export interface AIClientOptions {
  endpoint?: string;
  /** If true, skip the network call and use the static fallback immediately. */
  disabled?: boolean;
}

export class AIClient {
  private endpoint: string;
  private disabled: boolean;

  constructor(options: AIClientOptions = {}) {
    this.endpoint = options.endpoint ?? "/api/narrate";
    this.disabled = options.disabled ?? false;
  }

  /** Disable AI globally (e.g. budget kill-switch or user preference). */
  setDisabled(value: boolean): void {
    this.disabled = value;
  }

  /**
   * Run an AI scene. Streams text into the terminal typewriter queue.
   * On completion, applies bounded effects to gameState.
   * Falls back to static text on any error.
   */
  async runScene(req: AISceneRequest, terminal: Terminal): Promise<void> {
    if (this.disabled) {
      this.useFallback(req.scene, terminal);
      return;
    }

    const snap = gameState.snapshot();
    const body = JSON.stringify({
      scene: req.scene,
      playerInput: req.playerInput,
      state: {
        rep: snap.stats.rep,
        intel: snap.stats.intel,
        muscle: snap.stats.muscle,
        heat: snap.stats.heat,
        day: snap.day,
        flags: snap.flags,
      },
    });

    try {
      const res = await fetch(this.endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        signal: AbortSignal.timeout(30_000),
      });

      if (!res.ok || !res.body) {
        throw new Error(`HTTP ${res.status}`);
      }

      await this.consumeStream(res.body, req.scene, terminal);
    } catch (err) {
      // Log but never break the game.
      console.warn("[aiClient] request failed, using fallback:", err);
      this.useFallback(req.scene, terminal);
    }
  }

  private async consumeStream(
    body: ReadableStream<Uint8Array>,
    scene: string,
    terminal: Terminal
  ): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let gotDone = false;

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE lines are separated by "\n\n"
        const parts = buffer.split("\n\n");
        buffer = parts.pop() ?? "";

        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const raw = line.slice(5).trim();
          if (raw === "[DONE]") {
            gotDone = true;
            continue;
          }
          try {
            const parsed: unknown = JSON.parse(raw);
            if (
              typeof parsed === "object" &&
              parsed !== null &&
              "done" in parsed &&
              (parsed as AIDoneEvent).done === true
            ) {
              // Final event — apply bounded effects.
              const ev = parsed as AIDoneEvent;
              const safe = clampEffects(ev.effects);
              gameState.applyEffects(safe);
              // Only allow flags from the scene's allowlist (server enforces too).
              if (Array.isArray(ev.flags_add)) {
                gameState.addFlags(ev.flags_add);
              }
              gotDone = true;
            } else if (
              typeof parsed === "object" &&
              parsed !== null &&
              "text" in parsed
            ) {
              // Streaming text chunk.
              const chunk = (parsed as { text: string }).text;
              if (typeof chunk === "string" && chunk.length > 0) {
                terminal.print({ text: chunk, class: "npc" });
              }
            }
          } catch {
            // Malformed SSE data — skip.
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    if (!gotDone) {
      // Stream ended without a done event — use fallback for safety.
      console.warn("[aiClient] stream ended without done event, using fallback");
      this.useFallback(scene, terminal);
    }
  }

  private useFallback(scene: string, terminal: Terminal): void {
    const text = FALLBACKS[scene] ?? "The silence stretches. No answer comes.";
    terminal.print({ text, class: "npc" });
  }
}

/** Singleton AI client used throughout the game. */
export const aiClient = new AIClient();
